import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { get, throttle } from 'lodash'
import { connect } from 'react-redux'
import { push } from 'connected-react-router'
import { injectIntl } from 'react-intl'
import { withRouter } from 'react-router'
import { getFormValues } from 'redux-form'
import store from 'store'
import queryString from 'query-string'

import { payment as paymentApi } from 'redux/modules/api'
import normalizeParticipant from 'helpers/normalizeParticipant'
import { notification } from 'redux/modules/app'
import { isValidAuthCode } from 'redux/modules/api/common'
import { mpgConfigSchema, participantScheme } from 'models'
import { apiParticipants, apiConfiguration } from 'helpers/decorators/api'
import { CONFIGURATION_KEYS } from 'constants/enums'
import configFile from 'config'

export default (config = { formName: 'paymentForm' }) => ComposedComponent => {
  @apiParticipants()
  @apiConfiguration()
  @connect(
    state => ({
      payment: state.api.payment,
      participant: state.api.payment.participant.data,
      normalizedParticipant: normalizeParticipant(state),
      localeId: state.app.locale.id,
      formValues: getFormValues(config.formName)(state)
    }),
    { ...paymentApi, isValidAuthCode, notification, push }
  )
  @withRouter
  @injectIntl
  class Payment extends Component {
    static propTypes = {
      currentLocale: PropTypes.string,
      getMasterCardSession: PropTypes.func,
      initGlobalPayments: PropTypes.func,
      getParticipantByAuthCode: PropTypes.func,
      createPaypalPayment: PropTypes.func,
      notification: PropTypes.func,
      intl: PropTypes.object,
      updateParticipantInfo: PropTypes.func,
      localeId: PropTypes.number,
      location: PropTypes.object,
      executePayment: PropTypes.func,
      push: PropTypes.func,
      onGetConfigurationValue: PropTypes.func,
      isValidAuthCode: PropTypes.func
    }

    static defaultProps = {
      params: {}
    }

    constructor(props) {
      super(props)
      this.authCode = null
      this.scriptId = '__mpgScript__'
      this.handleUpdateInfo = throttle(this.handleUpdateInfo, 1000)
      this.state = {
        isExpired: false,
        isConsentGiven: false
      }
    }

    setAuthCode = async(authCode, checkIfExpired = false) => {
      this.authCode = authCode

      if (!checkIfExpired) {
        return
      }

      return await this.handleCheckAuthCode()
    }

    createScript = (mpgUrl, mpgSession, participant) => {
      const { params } = this.props

      const script = document.createElement('script')

      script.src = mpgUrl
      script.id = this.scriptId

      script.setAttribute(
        'data-error',
        `${configFile.baseUrl}/mastercard-online-payments/failure`
      )
      script.setAttribute(
        'data-complete',
        `${configFile.baseUrl}/mastercard-online-payments/success?authCode=${get(params, 'authCode')}`
      )
      script.setAttribute(
        'data-cancel',
        `${configFile.baseUrl}/mastercard-online-payments/cancel`
      )
      script.onload = this.handleMPG(participant, mpgSession)
      document.head.appendChild(script)
    }


    initializeGp = async() => {
      const { params } = this.props

      try {
        await this.setAuthCode(params.authCode)

        const baseUrl = configFile.gpIframeBaseUrl || configFile.baseUrl
        const responseUrl = `${baseUrl}/gp-iframe-result`

        const { body: hppData } = await this.props.initGlobalPayments(this.authCode, responseUrl)

        return hppData
      } catch (err) {
        console.error(err)
        this.handleError(err)
        throw err
      }
    }


    processGpPayment = async responseData => {
      const { params } = this.props
      const authCode = get(params, 'authCode')
      const orgId = get(params, 'orgId')

      if (!authCode || !orgId) {
        this.props.notification('Authorisation code is missing', {
          intlKey: 'notifications.authorization_code_is_missing',
          status: 'error'
        })

        return this.props.push(`${configFile.publicPath}/gp-error`)
      }

      try {
        if (!responseData) {
          throw Error('Payment data not received')
        }

        await this.props.processGlobalPayments(authCode, responseData)

        return this.props.push(`${configFile.publicPath}/gp-success`)
      } catch (err) {
        console.error(err)
        this.props.push(`${configFile.publicPath}/gp-error/${orgId}/${authCode}`)
        this.handleError(err)
      }
    }


    initializeGpWp = async() => {
      const { params } = this.props

      try {
        await this.setAuthCode(params.authCode)

        const responseUrl = `${configFile.baseUrl}/gp-wp-result/${this.authCode}`

        const { body: hppData } = await this.props.initGpWp(this.authCode, responseUrl)

        delete hppData.MESSAGETOSIGN

        const redirectUrl = `${configFile.gpWpUrl}?${queryString.stringify(hppData)}`
        window.location.replace(redirectUrl)

        return hppData
      } catch (err) {
        console.error(err)
        this.handleError(err)
        throw err
      }
    }


    processGpWp = async() => {
      const { params } = this.props
      const authCode = get(params, 'authCode')
      const data = queryString.parse(get(this.props, 'location.search'))

      if (!authCode) {
        this.props.notification('Authorisation code is missing', {
          intlKey: 'notifications.authorization_code_is_missing',
          status: 'error'
        })

        return false
      }

      try {
        await this.props.processGpWp(authCode, data)

        return true
      } catch (err) {
        console.errfor(err)
        this.handleError(err)
        throw err
      }
    }


    intializeMpg = async() => {
      const scriptExists = document.getElementById(this.scriptId)

      if (scriptExists) {
        console.log('exists')
        return
      }

      const { params } = this.props

      try {
        get(params, 'authCode') && (await this.setAuthCode(params.authCode))
        const [
          { body: participant },
          { body: mpgSession }
        ] = await Promise.all([
          this.handleGetParticipant(),
          this.props.getMasterCardSession(this.authCode)
        ])
        const { body: mpgUrlConfig } = await this.props.onGetConfigurationValue(
          CONFIGURATION_KEYS.payCheckoutUrl.id,
          get(participant, 'organizationId')
        )
        const mpgUrl = get(mpgUrlConfig, 'textValue')

        this.createScript(mpgUrl, mpgSession, participant)
      } catch (err) {
        console.error(err)
        this.handleError(err)
      }
    }

    intializePaypal = async() => {
      const { params } = this.props

      try {
        const { body = {} } = await this.props.createPaypalPayment(
          get(params, 'authCode')
        )

        if (body.approvalUrl) {
          store.set('authCode', get(params, 'authCode'))
          window.location.replace(body.approvalUrl)
        }
      } catch (err) {
        console.error(err)
        this.handleError(err)
        this.props.push(`${configFile.publicPath}/`)
      }
    }

    proccessPaypalPayment = async() => {
      const authCode = store.get('authCode')

      // this is just a quick fix, because this function was called many times and it causes error on production
      if (!authCode) {
        return
      }

      store.remove('authCode')

      if (!authCode) {
        this.props.notification('Authorisation code is missing', {
          intlKey: 'notifications.authorization_code_is_missing',
          status: 'error'
        })

        return this.props.push(`${configFile.publicPath}/paypal/cancel`)
      }

      try {
        const query = queryString.parse(get(this.props, 'location.search'))
        const paymentId = get(query, 'paymentId')
        const payerId = get(query, 'PayerID')

        await this.props.executePayment(authCode, {
          paymentId,
          payerId
        })

        return this.props.push(`${configFile.publicPath}/paypal/success`)
      } catch (err) {
        console.error(err)
        this.props.push(`${configFile.publicPath}/paypal/cancel`)
        this.handleError(err)
      }
    }

    handleMPG = (participant, mpgSession) => async() => {
      if (!window.Checkout) {
        throw new Error('CHECKOUT NOT LOADED')
      }
      const { currentLocale, intl } = this.props

      try {
        // Create config
        const config = mpgConfigSchema({
          participant,
          mpgSession,
          currentLocale,
          intl
        })

        window.Checkout.configure(config)

        window.Checkout.showPaymentPage()
      } catch (err) {
        this.handleError(err)
      }
    }

    // isExpired = true
    handleCheckAuthCode = async() => {
      try {
        const { body: isExpired } = await this.props.isValidAuthCode(
          this.authCode
        )

        if (isExpired.value === false) {
          this.setState({ isConsentGiven: true })
          return true
        }
      } catch (err) {
        // this.handleError(err)
        this.setState({ isExpired: true })
        return true
      }
    }

    handleUpdateInfo = async data => {
      try {
        const { localeId } = this.props
        const participant = participantScheme(data, localeId)

        await this.props.updateParticipantInfo(participant, this.authCode)

        return await this.handleGetParticipant()
      } catch (err) {
        this.handleError(err)
      }
    }

    handleGetParticipant = () =>
      this.props.getParticipantByAuthCode(this.authCode).catch(this.handleError)

    handleError = err => {
      console.error(err)

      this.props.notification(
        err.body &&
        (err.body.userMessage ||
          err.body.developerMessage ||
          'Unknown error occured during the payment'),
        'error'
      )
    }

    render() {
      return (
        <ComposedComponent
          {...this.props}
          {...this.state}
          intializeMpg={this.intializeMpg}
          intializePaypal={this.intializePaypal}
          initializeGp={this.initializeGp}
          processGpPayment={this.processGpPayment}
          proccessPaypalPayment={this.proccessPaypalPayment}
          initializeGpWp={this.initializeGpWp}
          processGpWp={this.processGpWp}
          onGetParticipant={this.handleGetParticipant}
          onUpdateInfo={this.handleUpdateInfo}
          setAuthCode={this.setAuthCode}
        />
      )
    }
  }

  return Payment
}
